/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { scroller } from 'react-scroll';
import { useMediaQuery } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import { get, initial } from 'lodash';
import { scrollerSettings } from '../../../settings';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import { FormEntityStatus } from '../constants';
import { getConfirmedEntities, getNotConfirmedEntities } from '../../../shared/utils/submissionUtils';
import { callApi } from '../../../api/helpers/api.helpers';
import draftApi from '../../../api/draft.api';
import { OperationType } from '../../../shared/enums/OperationType';

export const LAYOUT = {
  GRID: 'GRID_VIEW',
  LIST: 'LIST_VIEW',
};

/**
 * Customized arrayFieldTemplate that adds configurable confirmation support (via an UI dialog) for the array operations:
 *    ADDING, EDITING, DELETING, REORDERING, CONFIRMING and CANCELING.
 *
 * Activating the popup is done by adding a configuration to the uiSchema ui:config property of the array.
 *
 * There are 3 possibilities to configure it:
 *  1. Fixed messages (dialog texts) for all the above operations (as in the example below)
 *  2. Fixed messages for only the defined operations:
 *      add to the popup object a property 'operationTypes' containing an array with the operations that require warning
 *      (values from the enum OperationType)
 *  3. Use custom texts and logic for each operation type:
 *      set the 'useCustomTexts' property to 'true'
 *      provide the 'customRuleName' of the Camunda rule that returns the popup texts when the warning should appear
 *      (the *Text fields of the config should be removed as they are not used)
 *
 * E.g.:
 *  "priorities": {
 *     "ui:title": "i18n(Priorities)",
 *     ...
 *     "ui:config": {
 *       "popup": {
 *         "enabled": true,
 *         "type": "warning",
 *         "titleText": "i18n(parties.update.default.popup.title)",
 *         "messageText": "i18n(parties.update.default.popup.msg)",
 *         "confirmButtonText": "i18n(parties.update.default.popup.confirm)",
 *         "cancelButtonText": "i18n(parties.update.default.popup.cancel)"
 *       }
 *     }
 *   }
 */
export default function useFormEntitiesArrayTemplate({
  formData,
  title,
  idSchema,
  items,
  uiSchema,
  registry,
  onAddClick,
  formContext,
}) {
  const { submission } = formContext;
  const [cachedEntities, setCachedEntities] = useState({});

  const confirmedEntities = useMemo(() => getConfirmedEntities(items), [items]);
  const notConfirmedEntities = useMemo(() => getNotConfirmedEntities(items), [items]);

  const summarySectionId = `summary_cards_${idSchema.$id}`;
  const btnAddId = `btn_add_${idSchema.$id}`;
  const parentSectionName = initial(idSchema.$id.split('_')).pop();
  const sectionName = idSchema.$id.split('_').pop();

  // property to mark a section element can update other elements/inputs (siblings elements) in the same section
  const isSectionEditedByComponents = get(uiSchema, `${uiSchemaProperties.UI_COMPONENT_EDITABLE}`);

  const isMediumOrBigger = useMediaQuery(theme => theme.breakpoints.up('md'));
  const [isEditingEntity, setIsEditingEntity] = useState(false);
  const [layout, setLayout] = useState(isListView() ? LAYOUT.LIST : LAYOUT.GRID);
  const [entityId, setEntityId] = useState(null);
  const [entityOrderingData, setEntityOrderingData] = useState(null);

  // confirmation dialog
  const [isDialogCardOpen, setIsDialogCardOpen] = useState(false);
  const [popupConfig, setPopupConfig] = useState(uiSchema[uiSchemaProperties.UI_CONFIG]?.popup);

  const [operationType, setOperationType] = useState('');

  // handle state for the ADD item button
  useEffect(() => {
    if (notConfirmedEntities.length) {
      setIsEditingEntity(true);
    } else if (uiSchema[uiSchemaProperties.UI_READONLY]) {
      setIsEditingEntity(true);
    } else {
      setIsEditingEntity(false);
    }
  }, [uiSchema[uiSchemaProperties.UI_READONLY], notConfirmedEntities?.length]);

  const closeDialogCard = async () => {
    setEntityId(null);
    setIsDialogCardOpen(false);
    setOperationType(null);
    setEntityOrderingData(null);
  };

  const confirmDialogCard = async (event) => {
    if (operationType === OperationType.DELETE || !operationType) {
      await deleteEntity(entityId);
    } else if (operationType === OperationType.ADD) {
      await addEntity(event);
    } else if (operationType === OperationType.ORDER && entityOrderingData) {
      const { propertyPath, formEntities } = entityOrderingData;
      await reorderEntities(formEntities, propertyPath);
    } else if (operationType === OperationType.EDIT) {
      await editEntity(entityId);
    }

    await closeDialogCard();
  };

  const isConfirmedWithDialog = async (_submission, _entityPath, _operationType, entityIds) => {
    if (popupConfig?.enabled) {
      // allow the use of custom texts and logic by using Camunda custom rules
      if (popupConfig.useCustomTexts) {
        const customRuleParams = {
          entityPath: _entityPath,
          operationType: _operationType,
          entityIds: JSON.stringify(entityIds),
        };
        const ruleResult = await callApi(draftApi.runCustomRule)(
          submission,
          popupConfig.customRuleName,
          customRuleParams,
        );

        if (ruleResult) {
          setPopupConfig({
            ...popupConfig,
            ...ruleResult,
          });
        } else {
          return false;
        }
      }

      if (!popupConfig.operationTypes || popupConfig.operationTypes.includes(_operationType)) {
        setOperationType(_operationType);
        setIsDialogCardOpen(true);
        return true;
      }
    }

    return false;
  };

  // array item delete
  const onDelete = async (id) => {
    setEntityId(id);
    const entityPath = idSchema.$id;

    if (!(await isConfirmedWithDialog(submission, entityPath, OperationType.DELETE, [id]))) {
      await deleteEntity(id);
    }
  };

  const deleteEntity = async (id) => {
    await registry.onSectionDelete({
      sectionName,
      rootSection: parentSectionName,
      itemId: id,
    });
  };

  // adding item to array
  const onAdd = async (evt) => {
    const entityPath = idSchema.$id;
    if (!(await isConfirmedWithDialog(submission, entityPath, OperationType.ADD, []))) {
      await addEntity(evt);
    }
  };

  const addEntity = async (event) => {
    scroller.scrollTo(btnAddId, scrollerSettings);
    await onAddClick(event);
  };

  // reordering array items
  const onReorder = async (formEntities, propertyPath) => {
    setEntityOrderingData({ formEntities, propertyPath });
    const entityPath = idSchema.$id;
    const formEntityIds = formEntities.map(item => item.id);
    if (!(await isConfirmedWithDialog(submission, entityPath, OperationType.ORDER, formEntityIds))) {
      await reorderEntities(formEntities, propertyPath);
    }
  };

  const reorderEntities = async (formEntities, propertyPath) => {
    await registry.onSectionReorder({
      propertyPath,
      formEntities,
      sectionName,
      itemId: idSchema.$id,
    });
  };

  // editing array item
  const onEdit = async (id) => {
    setEntityId(id);
    const entityPath = idSchema.$id;
    setCachedEntities({
      ...cachedEntities,
      [id]: formData.find(p => p.id === id),
    });

    if (!(await isConfirmedWithDialog(submission, entityPath, OperationType.EDIT, [id]))) {
      await editEntity(id);
    }
  };

  const editEntity = async (id) => {
    await registry.onSectionEdit({
      sectionName,
      rootSection: parentSectionName,
      itemId: id,
      callback: () => scroller.scrollTo(summarySectionId, scrollerSettings),
    });
  };

  // confirming entity sub-form
  const onConfirm = async (element, customValidateSuccessHandler) => {
    // TODO Avoid this assignment please
    // eslint-disable-next-line no-param-reassign
    if (formData[element.index].id == null) formData[element.index] = { ...formData[element.index], id: uuidv4() };
    await registry.onSectionSubmit({
      customValidateSuccessHandler,
      sectionItemFormData: formData[element.index],
      propertyPath: `${idSchema.$id}_${element.index}_`,
      callback: () => scroller.scrollTo(summarySectionId, scrollerSettings),
      cancelSubmission: false,
    });
  };

  // canceling entity sub-form
  const onCancel = useCallback(
    async (element) => {
      if (formData[element.index].sequenceNumber) {
        // canceling a sub-form within a section with existing submitted entries
        const sectionItemFormData = cachedEntities[formData[element.index].id] || {
          ...formData[element.index],
          state: FormEntityStatus.CONFIRMED,
        };
        await registry.onSectionSubmit({
          sectionItemFormData,
          cancelSubmission: true,
          propertyPath: `${idSchema.$id}_${element.index}_`,
          callback: () => scroller.scrollTo(summarySectionId, scrollerSettings),
        });
      } else {
        const newSectionCancelCallback = async () => {
          scroller.scrollTo(summarySectionId, scrollerSettings);
          if (isSectionEditedByComponents) {
            // then we need to take care when we cancel this type of edited section and we should delete it manually
            await registry.onSectionDelete({
              sectionName,
              rootSection: parentSectionName,
              itemId: formData[element.index].id,
            });
          }
        };

        // canceling a sub-form within a section with no existing submitted entries
        element.onDropIndexClick(element.index)();
        registry.onNewSectionCancel({
          propertyPath: `${idSchema.$id}_${element.index}_`,
          callback: () => newSectionCancelCallback(),
        });
      }
    },
    [
      formData,
      cachedEntities,
      registry.onSectionSubmit,
      registry.onNewSectionCancel,
      idSchema,
    ],
  );

  function isListView() {
    if (get(uiSchema, `${uiSchemaProperties.UI_HIDE_LAYOUT_SWITCHER}`)) return false;
    return isMediumOrBigger && confirmedEntities.length > 3;
  }

  useEffect(() => {
    setLayout(isListView() ? LAYOUT.LIST : LAYOUT.GRID);
  }, [isListView()]);

  return {
    // array form entities
    confirmedEntities,
    notConfirmedEntities,
    // entity operation handlers
    onAdd,
    onEdit,
    onDelete,
    onReorder,
    onConfirm,
    onCancel,
    // confirmation dialog
    popupConfig,
    isDialogCardOpen,
    confirmDialogCard,
    closeDialogCard,
    // layout
    layout,
    setLayout,
    isMediumOrBigger,
    // misc
    uiSchema,
    title,
    btnAddId,
    summarySectionId,
    isEditingEntity,
  };
}
